import { AuthForm } from '@/components/auth/AuthForm';
import { GoogleSignInButton } from '@/components/auth/GoogleSignInButton';
import { MessageDisplay } from '@/components/auth/MessageDisplay';
import { Logo } from '@/components/shared/Logo';
import { useAuth } from '@/contexts/AuthContext';
import { useState, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export const AuthPage = (): ReactElement => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const { signInWithEmail } = useAuth();

  const handleLogin = async (emailInput: string, password?: string) => {
    setIsLoading(true);

    try {
      const { data, error } = await signInWithEmail(emailInput, password);

      if (error) {
        setMessage('An error occurred during login. Please try again.');
        setMessageType('error');
      } else {
        if (password) {
          // If we have data.session, the password login was successful
          if (data?.session) {
            navigate('/');
          } else {
            setMessage('Invalid credentials. Please try again.');
            setMessageType('error');
          }
        } else {
          setMessage('Check your email for the magic link!');
          setMessageType('success');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setMessage('An error occurred during login. Please try again.');
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col items-center justify-center w-full">
      <div className="container relative h-full flex-col items-center justify-center grid lg:max-w-none lg:grid-cols-2 lg:px-0">
        <div className="relative hidden h-full flex-col bg-muted p-10 text-white dark:border-r lg:flex">
          <div className="absolute inset-0 bg-primary" />
          <div className="relative z-20 flex items-center text-lg font-medium">
            <Logo color="white" />
          </div>
          <div className="relative z-20 mt-auto">
            <p className="text-lg">
              Welcome to the future of social media marketing
            </p>
          </div>
        </div>
        <div className="p-8">
          <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
            <div className="flex flex-col space-y-2 text-center">
              <h1 className="text-2xl font-semibold tracking-tight">
                Create/Log in to your account
              </h1>
              <p className="text-sm text-muted-foreground">
                Enter your email below to create/log in to your account
              </p>
            </div>

            <AuthForm onEmailSubmit={handleLogin} isLoading={isLoading} />
            {message && (
              <MessageDisplay message={message} messageType={messageType} />
            )}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Or continue with
                </span>
              </div>
            </div>
            <div className="flex flex-col space-y-2">
              <GoogleSignInButton />
            </div>
            <p className="px-8 text-center text-sm text-muted-foreground">
              By clicking continue, you agree to our{' '}
              <Link
                to="/terms"
                className="underline underline-offset-4 hover:text-primary">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link
                to="/privacy"
                className="underline underline-offset-4 hover:text-primary">
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
